import type { PaginationParams } from 'v2/pages/settings/queries'
import type { RInvoicesPayload } from 'v2/types/api/billing'
import type {
  Channels,
  ViewLiveConversationsParams,
  ViewRecordsParams,
  ViewRecordsWithPagination,
} from 'v2/types/api/conversations'
import type { IntegrationSource } from 'v2/types/api/settings'

export const conversationQueryKeys = () => ({
  active: ['conversations', 'activeConversations'],
  invited: ['conversations', 'invitedContacts'],
  downloadFile: (fileName: string) => ['downloadFile', fileName],
  chunks: (convNumber?: string) => ['chunks', convNumber || ''],
  allConversations: ['conversations'],
  views: (uuid?: string) => ['views', uuid || ''],
  conversationRecordings: (conv_number?: string) => ['recordings', conv_number || ''],
  embedRunRecording: (runId?: string) => ['embed-run-recording', runId || ''],
  conversationDetail: (conversationNumber: string) => ['conversationDetail', conversationNumber],
  handoffContext: (conversationNumber: string) => ['handoffContext', conversationNumber],
  handoffContextConnection: ['handoffContextConnection'],
  handoffCallPreviewBase: ['handoffCallPreview'],
  handoffCallPreview: (conversationNumber: string) => ['handoffCallPreview', conversationNumber],
  conversationFilterConfiguration: ['conversationFilterConfiguration'],
  recentContacts: (channel: Channels, senderId?: string) => [
    'recentContacts',
    channel,
    senderId || '',
  ],
  liveAgents: (channel?: Channels) => ['liveAgents', channel || ''],
  liveTeams: (channel?: Channels) => ['liveTeams', channel || ''],
  viewRecords: (viewUuid: string, filters?: ViewRecordsParams) => [
    'viewRecords',
    viewUuid,
    filters?.search_query,
    filters?.page,
    filters?.filters,
  ],
  tasks: (viewName: string, filters: ViewRecordsWithPagination) => [
    'tasks',
    viewName,
    filters?.search_query,
    filters?.pagination?.pageIndex,
    filters?.pagination?.pageSize,
    filters?.filters,
  ],
  conversationTasks: (conversationNumber: string) => ['conversationTasks', conversationNumber],
  taskActivities: (taskUuid: string) => ['taskActivities', taskUuid],
  getLiveConversationInViews: (payload: ViewLiveConversationsParams) => [
    'live-conversations',
    payload,
  ],
})

export const conversationMutationKeys = () => ({
  transfer: ['transfer'],
  leaveMonitoring: ['leaveMonitoring'],
  startMonitoring: ['startMonitoring'],
})

export const onboardingMutationKeys = () => ({
  complete: ['completeOnboarding'],
})

export const getSettingsQueryKeys = () => ({
  status: ['status'],
  flows: ['flow'],
  flowsByType: (type?: string) => ['flow', type],
  flowOptionsBase: ['flow', 'options'],
  // useRemoteSelect appends search term + page params to this key itself
  flowOptions: (type?: string, status?: string) => ['flow', 'options', type, status],
  queues: ['queues'],
  teams: ['teams'],
  roles: ['roles'],
  skills: ['skills'],
  verifiedCallerIds: ['verifiedCallerIds'],
  scopeTokens: ['scopeTokens'],
  sandboxNumbersBase: ['sandboxNumbers'],
  sandboxNumbers: ['sandboxNumbers', 'list'],
  holidays: (uuid: string) => ['holidays', uuid],
  statusConfigurations: ['statusConfigurations'],
  profilePermission: ['profilePermission'],
  businessHours: ['businessHours'],
  profile: ['profile'],
  keyContacts: ['keyContacts'],
  sipCredentials: ['sipCredentials'],
  listOrganizations: ['listOrganizations'],
  routingSettings: ['routingSettings'],
  users: ['users'],
  supportUserStatus: ['supportUserStatus'],
  channelConfigByChannel: (channel: string) => ['channelConfig', channel],
  agentSettings: ['agentSettings'],
  accountNumbersBase: ['accountNumbers'],
  availableNumbersBase: ['availableNumbers'],
  accountNumbers: (params: Record<string, unknown>) => [
    ...getSettingsQueryKeys().accountNumbersBase,
    params,
  ],
  phoneNumbers: ['accountNumbers', 'phoneNumbers-v3'],
  phoneNumbersSelect: ['accountNumbers', 'phoneNumbers-v3', 'select'],
  availableNumbers: (country: string, search: string, numberType: string) => [
    ...getSettingsQueryKeys().availableNumbersBase,
    country,
    search,
    numberType,
  ],
  nonTeamMembersByTeam: (teamUUID?: string, paginationParams?: PaginationParams) => [
    'nonTeamMembers',
    teamUUID || 'all',
    paginationParams,
  ],
  internalNumbers: ['internalNumbers'],
  timezones: ['timezones', 'admin'],
  externalNumbers: ['externalNumbers'],
  dispositions: ['dispositions'],
  updateDisposition: ['updateDisposition'],
  conversationsConfiguration: ['conversationsConfiguration'],
  destinations: ['destinations'],
  holidayGroups: ['holiday-groups'],
  // PubNub
  pubnubTokenBase: ['pubnub-token'],
  pubnubToken: (orgId: string) => [...getSettingsQueryKeys().pubnubTokenBase, orgId],
  permissions: ['get-permissions'],
  domains: ['domains'],
  ssoConfig: ['sso-config'],
  configureNumberMutation: (number: string) => ['configureNumber', number],
  updateNumberCnamLookupMutation: ['updateNumberCnamLookup'],
  updateNumberCnamMutation: ['updateNumberCnam'],
  buyNumberMutation: ['buyNumber'],
  getAuthIdAndToken: ['getAuthIdAndToken'],
  ipWhitelist: ['settings', 'ip-whitelist'],
  senderIdsV2Base: ['senderIdsV2'],
  senderIdsV2: (params: Record<string, unknown>) => [
    ...getSettingsQueryKeys().senderIdsV2Base,
    params,
  ],
  senderIdCountryOptions: ['senderIdCountryOptions'],
  accountConcurrency: ['accountConcurrency'],
  usageLimit: (accountId: string) => ['usageLimit', accountId],
  allowedBuyCountriesBase: ['allowedBuyCountries'],
})

export const externalAppsQueryKeys = () => ({
  externalOrgApps: (isActive?: boolean) => ['external-org-apps', isActive ? 'active' : ''],
  externalOrgAppsBase: ['external-org-apps'],
  externalOrgAppsV3: (isActive?: boolean, mode?: IntegrationSource) => [
    'external-org-apps',
    isActive ? 'active' : '',
    'v3',
    ...(mode ? [mode] : []),
  ],
  externalAppsBase: ['external-apps'],
  externalApps: (mode?: IntegrationSource) => ['external-apps', ...(mode ? [mode] : [])],
  externalAppsV2: (mode?: IntegrationSource) => ['external-apps', 'v2', ...(mode ? [mode] : [])],
  oauthProvidersBase: ['external-apps', 'oauth-providers'],
  oauthProviders: (mode?: IntegrationSource) => [
    'external-apps',
    'oauth-providers',
    ...(mode ? [mode] : []),
  ],
  externalAppActionsBase: ['external-app-actions'],
  externalAppActions: (appId: string) => ['external-app-actions', appId],
  redirectionURL: (appId: string) => ['redirection-url', appId],
  summaryBase: ['summary'],
  summary: (appId: string, contactIdentifier: string, mode?: IntegrationSource) => [
    'summary',
    appId,
    contactIdentifier,
    ...(mode ? [mode] : []),
  ],
  entityDetailsBase: ['entity-details'],
  entityDetails: (
    appId: string,
    payload: Record<string, unknown>,
    actionName: string,
    mode?: IntegrationSource,
  ) => ['entity-details', appId, payload, actionName, ...(mode ? [mode] : [])],
  salesforceAccountBase: ['salesforce-account'],
  salesforceAccount: (
    salesforceContactId: string,
    salesforceAppId?: string,
    mode?: IntegrationSource,
  ) => ['salesforce-account', salesforceContactId, salesforceAppId, ...(mode ? [mode] : [])],
  customFieldsBase: ['custom-fields'],
  customFields: (appId: string, source?: IntegrationSource) => [
    'custom-fields',
    source ?? 'nango',
    appId,
  ],
  accessRecordsBase: ['access-records'],
  accessRecords: (orgAppUuid: string, source?: IntegrationSource) => [
    'access-records',
    source ?? 'nango',
    orgAppUuid,
  ],
})
export const flowQueryKeys = () => ({
  flowData: (phloID: string, phloVersion = 'latest') => ['flow-config', phloID, phloVersion],
  versions: (phloID: string) => ['flow-versions', phloID],
  execHttpRequest: ['exec-http-request'],
  businessHoursForFlow: ['business-hours-for-flow'],
  audioUrl: (url: string) => ['audio-url', url],
  channelConfigs: (flowId?: string) => ['channel-configs', flowId],
  appVariablesBase: ['app-variables'],
  appVariables: (mode?: IntegrationSource) => ['app-variables', ...(mode ? [mode] : [])],
  mcpToolsBase: ['mcp-tools'],
  mcpTools: (mode?: IntegrationSource) => ['mcp-tools', ...(mode ? [mode] : [])],
  mcpToolsForAppProvider: (appProvider: string) => ['mcp-tools', appProvider],
  mcpToolsForOrgAppUuid: (appProvider: string, orgAppUuid: string, mode?: IntegrationSource) => [
    'mcp-tools',
    appProvider,
    orgAppUuid,
    ...(mode ? [mode] : []),
  ],
  secretVariables: ['secret-variables'],
  flowsBase: ['flow'],
  paginatedFlows: ['flow', 'paginated-flows'],
  // Lightweight has-any-agents probe for the onboarding banner gate.
  flowsOnboardingProbe: ['flow', 'onboarding-probe'],
  paginatedFlowRuns: ['flow', 'paginated-flow-runs'],
  flowRunDetails: (flowRunId: string, evalSource?: string) =>
    evalSource
      ? ['flow', 'run-details', flowRunId, evalSource]
      : ['flow', 'run-details', flowRunId],
  knowledgeBases: ['knowledge-bases'],
  crawlStatus: (jobId: string) => ['crawl-status', jobId],
  documentsFromSourceBase: ['documents-from-source'],
  documentsFromSource: (source: string, sourceId: string) => [
    'documents-from-source',
    source,
    sourceId,
  ],
  documentFromSource: (source: string, sourceId: string, documentId: string) => [
    'document-from-source',
    source,
    sourceId,
    documentId,
  ],
  documentsFromSourceByFetchId: (fetchId: string) => ['documents-from-source-by-fetch-id', fetchId],
  knowledgeBase: (id: string) => ['knowledge-base', id],
  createKnowledgeBase: ['create-knowledge-base'],
  voices: (language: string, currentVoiceId?: string) => ['voices', language, currentVoiceId],
  textToSpeech: ['text-to-speech'],
  languages: ['languages'],
  providers: (languages?: string) => ['providers', languages],
  slackCreateAppLink: ['slack-create-app-link'],
  slackChannels: (botToken?: string) => ['slack-channels', botToken],
  generateNodeMetadata: ['generate-node-metadata'],
  communityVoices: (search?: string) => ['community-voices', search],
  simulationScenarios: (phloUuid: string) => ['simulation-scenarios', 'infinite', phloUuid],
  simulationRuns: (phloUuid: string) => ['simulation-runs', phloUuid],
  simulationRun: (runUuid: string) => ['simulation-run', runUuid],
  simulationRunScenario: (runUuid: string, scenarioUuid: string) => [
    'simulation-run-scenario',
    runUuid,
    scenarioUuid,
  ],
})

export const getExportRecordingQueryKeys = (
  viewUuid: string,
  conversationId: string,
  chunkId: string,
) => ({ exportRecording: ['exportRecording', viewUuid, conversationId, chunkId] })

export const transcriptionQueryKeys = (recordingUUID: string) => ['transcription', recordingUUID]

// Scoped per user+org so the cache re-fetches on account switch. Shared by useFeatureFlagsQuery and
// the tdlc-transport reader so the key never drifts between writer and reader.
export const featureFlagsQueryKey = (email?: string, orgName?: string) =>
  ['feature-flags', email, orgName] as const

export const billingQueryKeys = () => ({
  balance: (accountId: number, conversionRate: number) => [
    'billing-balance',
    accountId,
    conversionRate,
  ],
  // Prefix-match key for invalidating balance regardless of conversionRate.
  balanceAll: (accountId: number) => ['billing-balance', accountId] as const,
  // Transaction amounts are read in the currency each payment was charged in, so
  // the account's conversion rate is not part of this key.
  transactions: (accountId: number, limit = 20, offset = 0) => [
    'billing-transactions',
    accountId,
    limit,
    offset,
  ],
  // Prefix-match key for invalidating transactions regardless of pagination.
  transactionsAll: (accountId: number) => ['billing-transactions', accountId] as const,
  invoices: (payload: RInvoicesPayload, conversionRate: number, isIndian: boolean) => [
    'billing-invoices',
    payload,
    conversionRate,
    isIndian,
  ],
  cards: (accountId: number) => ['billing-cards', accountId],
  address: (accountId: number) => ['billing-address', accountId],
  rechargeDetails: (accountId: number, conversionRate: number) => [
    'billing-recharge-details',
    accountId,
    conversionRate,
  ],
  usageSummary: (accountId: number, fromDate: string, conversionRate: number) => [
    'billing-usage-summary',
    accountId,
    fromDate,
    conversionRate,
  ],
  usageSummaryAll: (accountId: number) => ['billing-usage-summary', accountId] as const,
  // month keys the cache to the current billing period; the window itself is chosen upstream.
  usageMonthlyTotal: (accountId: number, month: string, conversionRate: number) => [
    'billing-usage-monthly-total',
    accountId,
    month,
    conversionRate,
  ],
  receiptPDFDetails: (authId: string, uuid: string) => ['receipt-pdf', authId, uuid],
  balanceAlerts: (accountId: number, conversionRate: number) => [
    'billing-alerts',
    accountId,
    conversionRate,
  ],
  billingEmail: (accountId: number) => ['billing-email', accountId],
  auditLogs: () => ['billing-audit-logs'],
  accountDetails: (accountId: number) => ['billing-account-details', accountId],
  gstValidation: (gstNumber: string) => ['gst-validation', gstNumber],
  zipCodeValidation: (zipCode: string, country: string, state: string) => [
    'zip-code-validation',
    zipCode,
    country,
    state,
  ],
  pricing: (countryIso: string) => ['billing-pricing', countryIso],
  zentrunkPricing: (countryIso: string) => ['billing-zentrunk-pricing', countryIso],
  // USD Bank Transfer
  wireTransferDetails: (accountId: number) => ['billing-wire-transfer-details', accountId],
  // ACH / Bank Direct Debit
  bankAccount: (accountId: number) => ['billing-bank-account', accountId],
  achStatus: (accountId: number) => ['billing-ach-status', accountId],
  autoDebitSchedules: (accountId: number, bankMappingId: number) => [
    'billing-auto-debit-schedules',
    accountId,
    bankMappingId,
  ],
  // Cashfree (India PSP)
  // Base helpers — pass to invalidateQueries for prefix-match invalidation
  // across all paginations of the same list.
  cashfreeVbanksAll: () => ['billing-cashfree-vbanks'] as const,
  cashfreeSubscriptionsAll: () => ['billing-cashfree-subscriptions'] as const,
  cashfreeVbanks: (accountId: number, limit: number, offset: number) => [
    'billing-cashfree-vbanks',
    accountId,
    limit,
    offset,
  ],
  cashfreeVbankPayments: (vbankId: string, limit: number, offset: number) => [
    'billing-cashfree-vbank-payments',
    vbankId,
    limit,
    offset,
  ],
  razorpayVbank: (vbankId: string) => ['billing-razorpay-vbank', vbankId],
  razorpayVbankPayments: (vbankId: string) => ['billing-razorpay-vbank-payments', vbankId],
  cashfreeSubscriptions: (accountId: number, limit: number, offset: number) => [
    'billing-cashfree-subscriptions',
    accountId,
    limit,
    offset,
  ],
  cashfreeCards: (accountId: number) => ['billing-cashfree-cards', accountId] as const,
})

// module-level, not derived via getAPIConsoleQueryKeys(): a plain (non-function)
// property referencing the function from inside its own object literal makes
// TS infer the function's return type as circular (TS7023).
const ORIGINATION_URIS_BASE = ['api-console', 'origination-uris']

export const getAPIConsoleQueryKeys = () => ({
  subaccounts: ['api-console', 'subaccounts'],
  xmlApplications: ['api-console', 'xml-applications'],
  trunks: ['api-console', 'trunks'],
  inboundTrunks: ['api-console', 'trunks', 'inbound'],
  outboundTrunks: ['api-console', 'trunks', 'outbound'],
  // Which SIP platform each trunk was set up for (core-recorded, one map per org)
  sipTrunkPlatformsBase: ['api-console', 'trunks', 'platforms'],
  sipTrunkPlatforms: ['api-console', 'trunks', 'platforms', 'all'],
  // Lightweight has-any-resource probes for the onboarding banner gates.
  trunksOnboardingProbe: ['api-console', 'trunks', 'onboarding-probe'],
  applicationsOnboardingProbe: ['api-console', 'xml-applications', 'onboarding-probe'],
  outboundAccessStatus: ['api-console', 'outbound-access-status'],
  zentrunkEnabled: ['api-console', 'zentrunk', 'enabled'],
  applications: ['api-console', 'applications'],
  endpointsBase: ['api-console', 'endpoints'],
  endpointDetails: (endpointId: string) => ['api-console', 'endpoints', endpointId],

  // Rental Summary
  rentalSummaryBase: ['api-console', 'rental-summary'],

  // Unrented Numbers
  unrentedNumbersBase: ['api-console', 'unrented-numbers'],

  // Port-in Orders
  portInOrdersBase: ['api-console', 'port-in-orders'],
  portInOrders: (params: Record<string, string>) => [
    ...getAPIConsoleQueryKeys().portInOrdersBase,
    JSON.stringify(params),
  ],
  portInOrdersExport: (params: Record<string, string>) => [
    ...getAPIConsoleQueryKeys().portInOrdersBase,
    'export',
    JSON.stringify(params),
  ],
  portInPhoneNumberValidation: (
    phoneNumber: string,
    phoneNumberType?: string,
    countryIso2?: string,
    requestId?: string,
  ) => [
    'api-console',
    'port-in-phone-number-validation',
    phoneNumber,
    phoneNumberType,
    countryIso2,
    requestId,
  ],
  portInOrderDetails: (requestId: string | null) => [
    ...getAPIConsoleQueryKeys().portInOrdersBase,
    'details',
    requestId,
  ],
  portInNumberConfigDetails: (requestId: string | null) => [
    ...getAPIConsoleQueryKeys().portInOrdersBase,
    'number-config-details',
    requestId,
  ],

  // Geo Permissions
  geoPermissionsBase: (subaccountAuthId?: string) =>
    subaccountAuthId
      ? ['api-console', 'geo-permissions', subaccountAuthId]
      : ['api-console', 'geo-permissions', 'all'],
  sipTrunkingGeoPermissionsBase: () => ['api-console', 'sip-trunking-geo-permissions'],
  saveGeoPermission: (subaccountAuthId?: string) =>
    subaccountAuthId
      ? ['api-console', 'geo-permissions', 'save', subaccountAuthId]
      : ['api-console', 'geo-permissions', 'save', 'all'],

  // Call logs
  callLogsBase: ['api-console', 'call-logs'],
  callLogs: (params: Record<string, string>) => [
    ...getAPIConsoleQueryKeys().callLogsBase,
    JSON.stringify(params),
  ],
  callInsights: (callUuid: string, endDate: string) => [
    ...getAPIConsoleQueryKeys().callLogsBase,
    'insights',
    callUuid,
    endDate,
  ],
  audioStats: (callUuid: string, endDate: string) => [
    ...getAPIConsoleQueryKeys().callLogsBase,
    'audio-stats',
    callUuid,
    endDate,
  ],
  callDebugLogs: (callUuid: string) => [
    ...getAPIConsoleQueryKeys().callLogsBase,
    'debug-logs',
    callUuid,
  ],
  callRawLogs: (callUuid: string) => [
    ...getAPIConsoleQueryKeys().callLogsBase,
    'raw-logs',
    callUuid,
  ],

  // Call Recordings
  callRecordings: (callUuid: string, params?: Record<string, unknown>) => [
    ...getAPIConsoleQueryKeys().callLogsBase,
    'recordings',
    callUuid,
    JSON.stringify(params || {}),
  ],

  // Recordings logs page (standalone list under Logs)
  recordingLogsBase: ['api-console', 'recording-logs'],
  recordingLogs: (params: Record<string, string>) => [
    ...getAPIConsoleQueryKeys().recordingLogsBase,
    JSON.stringify(params),
  ],

  // Call Transcriptions
  callTranscriptions: (callUuid: string, params?: Record<string, unknown>) => [
    ...getAPIConsoleQueryKeys().callLogsBase,
    'transcriptions',
    callUuid,
    JSON.stringify(params || {}),
  ],

  // Transcriptions logs page (standalone list under Logs)
  transcriptionLogsBase: ['api-console', 'transcription-logs'],
  transcriptionLogs: (params: Record<string, string>) => [
    ...getAPIConsoleQueryKeys().transcriptionLogsBase,
    JSON.stringify(params),
  ],

  // Message logs
  messageLogsBase: ['api-console', 'message-logs'],
  messageLogs: (params: Record<string, string>) => [
    ...getAPIConsoleQueryKeys().messageLogsBase,
    JSON.stringify(params),
  ],
  messageInsights: (messageUuid: string) => [
    ...getAPIConsoleQueryKeys().messageLogsBase,
    'insights',
    messageUuid,
  ],
  messageDebugLogs: (messageUuid: string, channel?: string, subaccountAuthId?: string | null) => [
    ...getAPIConsoleQueryKeys().messageLogsBase,
    'debug-logs',
    messageUuid,
    channel,
    subaccountAuthId ?? null,
  ],

  // WhatsApp logs
  whatsappLogsBase: ['api-console', 'whatsapp-logs'],
  whatsappLogs: (params: Record<string, string>) => [
    ...getAPIConsoleQueryKeys().whatsappLogsBase,
    JSON.stringify(params),
  ],

  // Voice Alerts
  voiceAlertsBase: (subaccountId?: number) =>
    subaccountId
      ? ['api-console', 'voice-alerts', `${subaccountId}`]
      : ['api-console', 'voice-alerts', 'all'],

  // Origination URIs
  originationURIsBase: ORIGINATION_URIS_BASE,
  zentrunkUris: [...ORIGINATION_URIS_BASE, 'list'],
  originationURI: (uriUuid: string | number) => [...ORIGINATION_URIS_BASE, `${uriUuid}`],

  // Zentrunk Logs
  zentrunkLogsBase: ['api-console', 'zentrunk-logs'],
  zentrunkLogs: (params: Record<string, string>) => [
    ...getAPIConsoleQueryKeys().zentrunkLogsBase,
    JSON.stringify(params),
  ],
  zentrunkCDRDetail: (callUuid: string) => [
    ...getAPIConsoleQueryKeys().zentrunkLogsBase,
    'detail',
    callUuid,
  ],

  // Credentials
  credentials: ['api-console', 'credentials'],

  // IP Access Control Lists
  ipAccessControlLists: ['api-console', 'ip-access-control-lists'],

  // SMS Delivery Alerts
  smsAlertSettings: () => ['api-console', 'sms-alert-settings'] as const,

  // SMS Other Settings
  smsOtherSettings: () => ['api-console', 'sms-other-settings'] as const,

  // Numbers Other Settings
  numbersOtherSettings: () => ['api-console', 'numbers-other-settings'] as const,

  // Voice Settings
  voiceSettings: (subaccountAuthId?: string) =>
    subaccountAuthId
      ? (['api-console', 'voice-settings', subaccountAuthId] as const)
      : (['api-console', 'voice-settings'] as const),

  // Zentrunk file capture consent (SIP Trunking Settings)
  zentrunkFileCaptureConsent: () => ['api-console', 'zentrunk-file-capture-consent'] as const,

  // SMS Geo Permissions
  smsGeoPermissionsBase: (subaccountAuthId?: string) =>
    subaccountAuthId
      ? ['api-console', 'sms-geo-permissions', subaccountAuthId]
      : ['api-console', 'sms-geo-permissions', 'all'],
  saveSmsGeoPermissions: (subaccountAuthId?: string) =>
    subaccountAuthId
      ? ['api-console', 'sms-geo-permissions', 'save', subaccountAuthId]
      : ['api-console', 'sms-geo-permissions', 'save', 'all'],
})

export const getVerifyAppQueryKeys = () => ({
  verifyApps: ['verifyApps'],
  verifyApp: (app_uuid: string | null) => ['verifyApp', app_uuid],
  verifyLogs: (params: Record<string, string>) => ['verifyLogs', JSON.stringify(params)],
  verifySession: (session_uuid: string | null) => ['verifySession', session_uuid],
})

export const queueQueryKeys = () => ({ queueDetails: (uuid: string) => ['queue-details', uuid] })

export const contactViewConfigQueryKeys = () => ({
  contactViewConfigBase: ['contactViewConfig'],
  contactViewConfig: (mode?: IntegrationSource) => ['contactViewConfig', ...(mode ? [mode] : [])],
  contactSummaryBase: ['contactSummary'],
  contactSummary: (contactIdentifier: string, mode?: IntegrationSource) => [
    'contactSummary',
    contactIdentifier,
    ...(mode ? [mode] : []),
  ],
})

export const tenDlcQueryKeys = () => ({
  all: ['10dlc'] as const,
  submissionsBase: () => [...tenDlcQueryKeys().all, 'submissions'] as const,
  submissions: (params: Record<string, string> = {}) => [
    ...tenDlcQueryKeys().submissionsBase(),
    JSON.stringify(params),
  ],
  submissionDetail: (uuid: string) => [...tenDlcQueryKeys().all, 'submission', uuid] as const,
  brandsBase: () => [...tenDlcQueryKeys().all, 'brands'] as const,
  brands: (params: Record<string, string> = {}) => [
    ...tenDlcQueryKeys().brandsBase(),
    JSON.stringify(params),
  ],
  brandDetail: (uuid: string) => [...tenDlcQueryKeys().all, 'brand', uuid] as const,
  campaignsBase: () => [...tenDlcQueryKeys().all, 'campaigns'] as const,
  campaigns: (params: Record<string, string> = {}) => [
    ...tenDlcQueryKeys().campaignsBase(),
    JSON.stringify(params),
  ],
  campaignDetail: (campaignId: string) =>
    [...tenDlcQueryKeys().all, 'campaign', campaignId] as const,
  senderIdsBase: () => [...tenDlcQueryKeys().all, 'sender-ids'] as const,
  senderIds: (params: Record<string, string> = {}) => [
    ...tenDlcQueryKeys().senderIdsBase(),
    JSON.stringify(params),
  ],
  profileDetail: (profileUuid: string) =>
    [...tenDlcQueryKeys().all, 'profile', profileUuid] as const,
  profilesBase: () => [...tenDlcQueryKeys().all, 'profiles'] as const,
  profiles: (params: Record<string, string> = {}) => [
    ...tenDlcQueryKeys().profilesBase(),
    JSON.stringify(params),
  ],
  tollfreeVerificationNumbers: (pattern?: string) =>
    [...tenDlcQueryKeys().all, 'tollfree-verification-numbers', pattern ?? ''] as const,
  tollfreeVerificationsBase: () => [...tenDlcQueryKeys().all, 'tollfree-verifications'] as const,
  tollfreeVerifications: (params: Record<string, string> = {}) => [
    ...tenDlcQueryKeys().tollfreeVerificationsBase(),
    JSON.stringify(params),
  ],
  tollfreeVerificationDetail: (uuid: string) =>
    [...tenDlcQueryKeys().all, 'tollfree-verification', uuid] as const,
  campaignNumbersBase: (campaignId: string) =>
    [...tenDlcQueryKeys().all, 'campaign-numbers', campaignId] as const,
  campaignNumbers: (campaignId: string, params: Record<string, string> = {}) => [
    ...tenDlcQueryKeys().campaignNumbersBase(campaignId),
    JSON.stringify(params),
  ],
  availableNumbersBase: (campaignId: string) =>
    [...tenDlcQueryKeys().all, 'available-numbers', campaignId] as const,
  availableNumbers: (campaignId: string, search = '', offset = 0) => [
    ...tenDlcQueryKeys().availableNumbersBase(campaignId),
    search,
    offset,
  ],
})

export const uccQueryKeys = () => ({
  complaintsBase: ['ucc-complaints'],
  callbackConfig: ['ucc-callback-config'],
  complaints: (params: {
    page_number: number
    page_size: number
    search?: string
    status?: string
  }) => [
    ...uccQueryKeys().complaintsBase,
    params.page_number,
    params.page_size,
    params.search || '',
    params.status || '',
  ],
})

export const getUsageReportsQueryKeys = () => ({
  voiceInsightsBase: ['usage-reports', 'voice-insights'],
  voiceInsights: (params: unknown) => [
    ...getUsageReportsQueryKeys().voiceInsightsBase,
    JSON.stringify(params),
  ],
  usageOverviewBase: ['usage-reports', 'usage-overview'],
  usageOverview: (params: unknown) => [
    ...getUsageReportsQueryKeys().usageOverviewBase,
    JSON.stringify(params),
  ],
  usageMessagingBase: ['usage-reports', 'usage-messaging'],
  usageMessaging: (params: unknown) => [
    ...getUsageReportsQueryKeys().usageMessagingBase,
    JSON.stringify(params),
  ],
  usageVoiceBase: ['usage-reports', 'usage-voice'],
  usageVoice: (params: unknown) => [
    ...getUsageReportsQueryKeys().usageVoiceBase,
    JSON.stringify(params),
  ],
  subaccounts: ['usage-reports', 'subaccounts'],
})

export const agentObservabilityQueryKeys = () => ({
  agentJudgesBase: ['agent-observability', 'agent-judges'],
  agentJudges: (agentId: string) => [...agentObservabilityQueryKeys().agentJudgesBase, agentId],
  recentSessionsBase: ['agent-observability', 'recent-sessions'],
  recentSessions: (agentId: string) => [
    ...agentObservabilityQueryKeys().recentSessionsBase,
    agentId,
  ],
})

// Split reports derive a different response shape from legacy reports. Version this cache
// namespace so persisted data and cross-tab broadcasts cannot hydrate an incompatible result.
export const voiceAgentsReportQueryBase = ['analytics', 'voice-agents-ai', 'split-v1'] as const

export const voiceAgentsReportQueryKey = <Scope extends unknown[]>(
  ...scope: Scope
): [...typeof voiceAgentsReportQueryBase, ...Scope] => [...voiceAgentsReportQueryBase, ...scope]
